/**
 * Step 4 — Schedule.
 *
 * Where the admin picks how often the import runs. Cadence vocabulary lives
 * on the root monitoring skill (per the 2026-05-06 amendment); the v1 picker
 * exposes every_30m / every_1h / daily — every_4h is on the spec list but the
 * backend currently rejects it via `UNSUPPORTED_CADENCES` so it is hidden
 * here until backend support lands.
 *
 * Source capabilities may declare `schedule_constraints`:
 *   - min_cadence — the most frequent cadence allowed for this source (rate
 *     limits, API politeness). Anything more frequent is disabled.
 *   - recommended_cadence — pre-selected as the default + flagged in the UI.
 *
 * When cadence === 'daily' the user also picks a UTC time-of-day. The raw
 * <input type="time"> here is a TODO swap: once `feat/time-input-component`
 * (PR #197) merges, replace with the canonical `<TimeInput>` so the user sees
 * a local-time preview + day-shift suffix automatically.
 *
 * Persisted into scheduleConfig:
 *   cadence: 'every_30m' | 'every_1h' | 'daily'
 *   time_of_day?: 'HH:MM' (UTC, required when cadence === 'daily')
 *   timezone: 'UTC' (implicit for v1; spec carries it explicitly for forward
 *     compatibility once daily-time-of-day in a non-UTC zone is needed)
 */
import React, { useEffect, useMemo } from 'react';

import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';
import { RadioGroup, RadioGroupItem } from '../../../ui/radio-group';
import type { ImportCapability } from '../../../../types/importCapabilities';

type Cadence = 'every_30m' | 'every_1h' | 'daily';

interface CadenceOption {
  value: Cadence;
  label: string;
  description: string;
}

/**
 * Ordered most-frequent to least-frequent.
 *
 * `every_4h` is in the root skill's spec cadence vocabulary but is listed in
 * the backend's `UNSUPPORTED_CADENCES` set (good-outcomes-api/app/services/
 * import_jobs.py) so it would fail validation at Save. Hidden from the
 * picker until the backend scheduler supports it; restore here when it does.
 */
const CADENCE_OPTIONS: CadenceOption[] = [
  { value: 'every_30m', label: 'Every 30 minutes', description: 'Highest cost, near-real-time imports.' },
  { value: 'every_1h', label: 'Hourly', description: 'Balanced — most sources sit here.' },
  { value: 'daily', label: 'Daily', description: 'Runs once a day at the time you pick below.' },
];

const CADENCE_VALUES = CADENCE_OPTIONS.map((o) => o.value);

/**
 * Returns the indices of cadences disabled by the source's min_cadence
 * constraint. Any cadence more frequent than min_cadence is disabled.
 */
const cadencesDisabledByMin = (
  minCadence: Cadence | null,
): Set<Cadence> => {
  if (!minCadence) return new Set();
  const minIdx = CADENCE_VALUES.indexOf(minCadence);
  if (minIdx < 0) return new Set();
  return new Set(CADENCE_VALUES.slice(0, minIdx));
};

const readCadenceConstraint = (
  capability: ImportCapability,
  key: 'min_cadence' | 'recommended_cadence',
): Cadence | null => {
  const constraints = capability.schedule_constraints;
  if (!constraints || typeof constraints !== 'object') return null;
  const raw = (constraints as Record<string, unknown>)[key];
  return typeof raw === 'string' && CADENCE_VALUES.includes(raw as Cadence)
    ? (raw as Cadence)
    : null;
};

interface ScheduleStepProps {
  capability: ImportCapability;
  values: Record<string, unknown>;
  onChange: (values: Record<string, unknown>) => void;
  onBack: () => void;
  onContinue: () => void;
}

const ScheduleStep: React.FC<ScheduleStepProps> = ({
  capability,
  values,
  onChange,
  onBack,
  onContinue,
}) => {
  const recommended = readCadenceConstraint(capability, 'recommended_cadence');
  const minCadence = readCadenceConstraint(capability, 'min_cadence');
  const disabled = useMemo(() => cadencesDisabledByMin(minCadence), [minCadence]);

  const currentCadence: Cadence | null =
    typeof values.cadence === 'string' &&
    CADENCE_VALUES.includes(values.cadence as Cadence)
      ? (values.cadence as Cadence)
      : null;
  const timeOfDay = typeof values.time_of_day === 'string' ? values.time_of_day : '';

  // Seed the recommended cadence (and an implicit UTC timezone) the first time
  // this step mounts with no prior cadence selection. Keeps required-validation
  // and the rendered radio in agreement, same pattern as Step 2's defaults.
  useEffect(() => {
    if (currentCadence === null && recommended && !disabled.has(recommended)) {
      onChange({ cadence: recommended, timezone: 'UTC' });
    } else if (!values.timezone) {
      onChange({ timezone: 'UTC' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canContinue = useMemo(() => {
    if (!currentCadence) return false;
    if (currentCadence === 'daily') {
      return /^([01]\d|2[0-3]):[0-5]\d$/.test(timeOfDay);
    }
    return true;
  }, [currentCadence, timeOfDay]);

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h2 className="text-lg font-semibold text-slate-900">Schedule</h2>
        <p className="text-sm text-slate-600">
          How often should this monitor run? Cadences faster than the source
          allows are disabled.
        </p>
      </header>

      <RadioGroup
        value={currentCadence ?? ''}
        onValueChange={(v) => onChange({ cadence: v })}
        className="gap-3"
      >
        {CADENCE_OPTIONS.map((opt) => {
          const isDisabled = disabled.has(opt.value);
          const isRecommended = recommended === opt.value;
          const id = `schedule-cadence-${opt.value}`;
          return (
            <div
              key={opt.value}
              className={[
                'flex items-start gap-3 rounded-lg border p-3',
                isDisabled
                  ? 'border-slate-100 bg-slate-50 opacity-60'
                  : 'border-slate-200 bg-white',
              ].join(' ')}
            >
              <RadioGroupItem
                value={opt.value}
                id={id}
                className="mt-0.5"
                disabled={isDisabled}
              />
              <Label
                htmlFor={id}
                className={[
                  'flex-1 cursor-pointer space-y-0.5',
                  isDisabled ? 'cursor-not-allowed' : '',
                ].join(' ')}
              >
                <span className="flex items-center gap-2">
                  <span className="text-sm font-medium text-slate-900">
                    {opt.label}
                  </span>
                  {isRecommended ? (
                    <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                      Recommended
                    </span>
                  ) : null}
                  {isDisabled ? (
                    <span className="text-xs text-slate-500">
                      Not allowed for this source
                    </span>
                  ) : null}
                </span>
                <span className="block text-xs text-slate-500">
                  {opt.description}
                </span>
              </Label>
            </div>
          );
        })}
      </RadioGroup>

      {currentCadence === 'daily' ? (
        <div className="space-y-1.5 rounded-lg border border-slate-200 bg-white p-4">
          <Label htmlFor="schedule-time-of-day">
            Daily time (UTC) <span className="ml-1 text-red-500">*</span>
          </Label>
          {/*
            TODO: swap to canonical <TimeInput> once feat/time-input-component
            (PR #197) lands on staging. TimeInput shows local-time preview +
            day-shift suffix, which is what we want here too.
          */}
          <Input
            id="schedule-time-of-day"
            type="time"
            value={timeOfDay}
            onChange={(e) => onChange({ time_of_day: e.target.value })}
          />
          <p className="text-xs text-slate-500">
            All daily runs are anchored to UTC. Local-time preview lands once
            the canonical TimeInput is on staging.
          </p>
        </div>
      ) : null}

      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button onClick={onContinue} disabled={!canContinue}>
          Continue
        </Button>
      </div>
    </div>
  );
};

export default ScheduleStep;
